import setVehicleAnnotations from "action/garage/setVehicleAnnotations";
import setVehicleImages from "action/garage/setVehicleImages";
import getVehicleImage from "api/garage/getVehicleImage";
import { SECONDARY_VEHICLE_SELECTION_OPTIONS } from "component/vehicle-form/constants";
import { getStore } from "store";
import { createVehicleDescription } from "util/vehicle/createVehicleDescription";
import { getFitmentOptionQS } from "util/vehicle/get-fitment-option-q-s";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import getDefaultVehicleImage from "./getDefaultVehicleImage";
import { getRelativeImagePath } from "./getRelativeImagePath";

const vehicleImageCache = {};
let firstMount = true;
let previousVehicle = null;

async function getVehicleImageUrl({
  vehicle, vehicleImageUrl = "", modal = false, getAllImages = false,
}: any): Promise<string> {
  const result = await _getVehicleImageUrl({
    vehicle, vehicleImageUrl, modal, getAllImages,
  });
  previousVehicle = vehicle;
  return result;
}

async function _getVehicleImageUrl({
  vehicle, vehicleImageUrl = "", modal = false, getAllImages = false,
}: any): Promise<string> {
  const defaultImage = getDefaultVehicleImage(vehicle);
  const store = getStore();
  const { garage } = store.getState();
  const { selectedVehicle } = garage;

  const vehicleDesc = createVehicleDescription(vehicle, true, true, true);
  const prevVehicleDesc = createVehicleDescription(previousVehicle, true, true, true);
  const selectedVehicleDesc = createVehicleDescription(selectedVehicle, true, true, true);

  const isVehicleSelected = validateVehicle(selectedVehicle) && selectedVehicleDesc === vehicleDesc;

  function defaultImageReturn(noImages = false) {
    if (!validateVehicle(selectedVehicle) || (isVehicleSelected && noImages)) {
      store.dispatch(setVehicleAnnotations(null));
      store.dispatch(setVehicleImages({
        small: defaultImage,
        medium: defaultImage,
        large: defaultImage,
      }));
    }
    return defaultImage;
  }

  const isNewVehicle = (vehicleDesc !== prevVehicleDesc);

  if (!validateVehicle(vehicle)) {
    return defaultImageReturn();
  }

  const selectedColorId = vehicle?.colorData?.colorId;
  const selectedColorName = vehicle?.colorData?.colorName;

  if (firstMount && !isNewVehicle && !modal && vehicleImageUrl && (vehicleImageUrl !== defaultImage) && !vehicle?.fitmentOptions?.length && !(selectedColorId || selectedColorName)) {
    return vehicleImageUrl;
  }

  firstMount = false;

  if (!firstMount && vehicleImageUrl === defaultImage) {
    return vehicleImageUrl;
  }

  const {
    year, make, model, fitmentOptions,
  } = vehicle;

  const filteredFitmentOptions = fitmentOptions?.filter(
    (option: any) => !SECONDARY_VEHICLE_SELECTION_OPTIONS.includes(option.optionName),
  );

  const fitmentOptionQS = getFitmentOptionQS(filteredFitmentOptions || []);
  const params = `year=${year}&make=${make}&model=${model}&${fitmentOptionQS}`;

  if (!vehicleImageCache[params]) {
    try {
      const res = await getVehicleImage(params);
      if (res?.data?.message === "NO_IMAGE_FOUND") {
        return defaultImageReturn(true);
      }
      vehicleImageCache[params] = res?.data || [];
    } catch (error) {
      return defaultImageReturn(true);
    }
  }

  const vehicleImages = vehicleImageCache[params]?.vehicleImages || [];
  const annotations = vehicleImageCache[params]?.annotations || {};

  if (isVehicleSelected) {
    store.dispatch(setVehicleAnnotations(annotations && Object.keys(annotations).length ? annotations : null));
  }

  // Default to White → Black → First Image
  let vehicleImage = vehicleImages.find(({ ShortName }) => ShortName === "White")
  || vehicleImages.find(({ ShortName }) => ShortName === "Black")
  || vehicleImages[0];

  // Override if user selected a color
  if (selectedColorId) {
    vehicleImage = vehicleImages.find(({ Id }) => Id === selectedColorId)
    || vehicleImages.find(({ Name }) => Name.includes(selectedColorName))
    || vehicleImage;
  }

  if (getAllImages) {
    return vehicleImages;
  }
  const angles = ["Img032", "Img014", "Img001"];
  const angle = angles.find((k) => vehicleImage?.[k]);

  const pickImage = (size: number) => {
    const img = angle ? vehicleImage?.[angle]?.[size] : defaultImage;
    return img === defaultImage ? defaultImage : getRelativeImagePath(img);
  };

  if (isVehicleSelected) {
    store.dispatch(setVehicleImages({
      small: pickImage(350), medium: pickImage(640), large: pickImage(960), angle,
    }));
  }

  return pickImage(350);
}

export default getVehicleImageUrl;
